import React, { useState, useEffect } from 'react';
import ENV_CONFIG from '../config/environment';

const UserData = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('today'); // today, date, month, year
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  
  // New state for task details popup
  const [taskDetailModal, setTaskDetailModal] = useState(null);
  const [modalTasks, setModalTasks] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Use environment configuration for API URL
  const API_BASE_URL = ENV_CONFIG.getBaseUrl();

  const apiCall = async (url, options = {}) => {
    const token = localStorage.getItem('authToken') || 
                  localStorage.getItem('token') || 
                  localStorage.getItem('access_token') ||
                  sessionStorage.getItem('authToken') ||
                  sessionStorage.getItem('token');
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    const defaultOptions = {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: defaultHeaders,
    };
    
    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {})
      }
    };
    
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    
    try {
      console.log('Making API request to:', fullUrl);
      console.log('Request options:', mergedOptions);
      
      const response = await fetch(fullUrl, mergedOptions);
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();
        console.log('Response data:', jsonData);
        return jsonData;
      } else {
        const textData = await response.text();
        console.log('Response text:', textData);
        return textData;
      }
    } catch (error) {
      console.error('API call failed:', error);
      
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new Error('Network error: Unable to connect to server. Please check if the server is running and CORS is configured properly.');
      }
      
      throw error;
    }
  };

  // Initialize with today's date
  useEffect(() => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
    setSelectedMonth(today.toISOString().slice(0, 7));
    setSelectedYear(today.getFullYear().toString());
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [filterType, selectedDate, selectedMonth, selectedYear]);

  const buildApiUrl = () => {
    let url = '/api/users/statistics/';
    const params = new URLSearchParams();

    switch (filterType) {
      case 'today':
        const today = new Date().toISOString().split('T')[0];
        params.append('date', today);
        break;
      case 'date':
        if (selectedDate) {
          params.append('date', selectedDate);
        }
        break;
      case 'month':
        if (selectedMonth) {
          params.append('month', selectedMonth);
        }
        break;
      case 'year':
        if (selectedYear) {
          params.append('year', selectedYear);
        }
        break;
      default:
        break;
    }

    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError('');
      const apiUrl = buildApiUrl();
      console.log('Fetching data from URL:', apiUrl);
      const userData = await apiCall(apiUrl);
      
      if (Array.isArray(userData)) {
        setUsers(userData);
      } else if (userData && typeof userData === 'object' && userData.data) {
        setUsers(Array.isArray(userData.data) ? userData.data : []);
      } else if (userData && typeof userData === 'object' && userData.results) {
        setUsers(Array.isArray(userData.results) ? userData.results : []);
      } else {
        console.warn('Unexpected data format:', userData);
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch user tasks
  const fetchUserTasks = async (userId, taskType) => {
    try {
      setModalLoading(true);
      setModalError('');
      
      // Build API URL for user tasks based on filter and task type
      let apiUrl = `/api/users/${userId}/tasks/`;
      const params = new URLSearchParams();
      
      // Add filter parameters
      switch (filterType) {
        case 'today':
          const today = new Date().toISOString().split('T')[0];
          params.append('date', today);
          break;
        case 'date':
          if (selectedDate) {
            params.append('date', selectedDate);
          }
          break;
        case 'month':
          if (selectedMonth) {
            params.append('month', selectedMonth);
          }
          break;
        case 'year':
          if (selectedYear) {
            params.append('year', selectedYear);
          }
          break;
      }
      
      // Add task type filter
      params.append('type', taskType);
      
      const queryString = params.toString();
      if (queryString) {
        apiUrl += `?${queryString}`;
      }
      
      const taskData = await apiCall(apiUrl);
      
      if (Array.isArray(taskData)) {
        setModalTasks(taskData);
      } else if (taskData && typeof taskData === 'object' && taskData.data) {
        setModalTasks(Array.isArray(taskData.data) ? taskData.data : []);
      } else if (taskData && typeof taskData === 'object' && taskData.results) {
        setModalTasks(Array.isArray(taskData.results) ? taskData.results : []);
      } else {
        console.warn('Unexpected task data format:', taskData);
        setModalTasks([]);
      }
    } catch (err) {
      console.error('Failed to fetch user tasks:', err);
      setModalError(err.message);
      setModalTasks([]);
    } finally {
      setModalLoading(false);
    }
  };

  // Handle task stat click
  const handleTaskStatClick = async (user, taskType, count) => {
    if (count === 0) return; // Don't open modal if no tasks
    
    const taskTypeLabels = {
      'created': 'Created',
      'assigned': 'Assigned', 
      'completed': 'Completed'
    };
    
    setTaskDetailModal({
      user: user,
      taskType: taskType,
      taskTypeLabel: taskTypeLabels[taskType],
      count: count
    });
    
    await fetchUserTasks(user.id, taskType);
  };

  // Close task detail modal
  const closeTaskDetailModal = () => {
    setTaskDetailModal(null);
    setModalTasks([]);
    setModalError('');
  };

  const handleFilterChange = (newFilterType) => {
    setFilterType(newFilterType);
    setError('');
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const getFilterDisplayText = () => {
    switch (filterType) {
      case 'today':
        return 'Today';
      case 'date':
        return selectedDate ? new Date(selectedDate).toLocaleDateString() : 'Select Date';
      case 'month':
        return selectedMonth ? new Date(selectedMonth + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Select Month';
      case 'year':
        return selectedYear || 'Select Year';
      default:
        return 'All Time';
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get status color helper
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'assigned': return 'status-assigned';
      case 'not started': return 'status-not-started';
      case 'in progress': return 'status-in-progress';
      case 'team review': return 'status-team-review';
      case 'client review': return 'status-client-review';
      case 'rejected by team': return 'status-rejected-team';
      case 'rejected by client': return 'status-rejected-client';
      case 'approved': return 'status-approved';
      case 'posted': return 'status-posted';
      case 'completed': return 'status-completed';
      default: return 'status-default';
    }
  };

  // Test connection function
  const testConnection = async () => {
    try {
      setError('Testing connection...');
      const response = await fetch(`${API_BASE_URL}/api/health/`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        setError('Connection successful! Retrying data fetch...');
        setTimeout(() => fetchUserData(), 1000);
      } else {
        setError(`Server responded with status: ${response.status}`);
      }
    } catch (err) {
      setError(`Connection test failed: ${err.message}`);
    }
  };

  if (loading) return <div className="loading">Loading user statistics...</div>;
  
  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchUserData} className="retry-btn">
              Retry
            </button>
            <button onClick={testConnection} className="test-btn">
              Test Connection
            </button>
          </div>
          <div className="troubleshooting">
            <h4>Troubleshooting:</h4>
            <ul>
              <li>Check if the backend server is running on {API_BASE_URL}</li>
              <li>Verify CORS is configured on the backend</li>
              <li>Check browser network tab for detailed error information</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-data-container">
      <div className="page-header">
        <h1>User Data & Statistics</h1>
      </div>

      {/* Date Filter Section */}
      <div className="filter-section">
        <div className="filter-content">
          <div className="filter-title">
            <span className="current-filter">Current: {getFilterDisplayText()}</span>
          
          <div className="filter-controls-inline">
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${filterType === 'today' ? 'active' : ''}`}
                onClick={() => handleFilterChange('today')}
              >
                Today
              </button>
              <button 
                className={`filter-btn ${filterType === 'date' ? 'active' : ''}`}
                onClick={() => handleFilterChange('date')}
              >
                Specific Date
              </button>
              <button 
                className={`filter-btn ${filterType === 'month' ? 'active' : ''}`}
                onClick={() => handleFilterChange('month')}
              >
                Month
              </button>
              <button 
                className={`filter-btn ${filterType === 'year' ? 'active' : ''}`}
                onClick={() => handleFilterChange('year')}
              >
                Year
              </button>
            </div>

            <div className="filter-inputs">
              {filterType === 'date' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="date-input"
                />
              )}
              {filterType === 'month' && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  className="date-input"
                />
              )}
              {filterType === 'year' && (
                <input
                  type="number"
                  value={selectedYear}
                  onChange={handleYearChange}
                  min="2020"
                  max="2030"
                  placeholder="Year"
                  className="date-input year-input"
                />
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      <div className="user-stats-grid">
        {users.map((user) => (
          <div key={user.id} className="user-stat-card">
            <div className="user-card-header">
              <div className="user-avatar">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <h3>{user.username}</h3>
                <span className={`user-role ${user.is_staff ? 'admin' : 'user'}`}>
                  {user.is_staff ? 'Admin' : 'User'}
                </span>
              </div>
            </div>

            <div className="user-stats">
              <div 
                className={`stat-item created ${user.tasks_created_count > 0 ? 'clickable' : ''}`}
                onClick={() => handleTaskStatClick(user, 'created', user.tasks_created_count)}
                title={user.tasks_created_count > 0 ? 'Click to view created tasks' : 'No created tasks'}
              >
                <div className="stat-number">{user.tasks_created_count || 0}</div>
                <div className="stat-label">Tasks Created</div>
              </div>

              <div 
                className={`stat-item assigned ${user.tasks_assigned_count > 0 ? 'clickable' : ''}`}
                onClick={() => handleTaskStatClick(user, 'assigned', user.tasks_assigned_count)}
                title={user.tasks_assigned_count > 0 ? 'Click to view assigned tasks' : 'No assigned tasks'}
              >
                <div className="stat-number">{user.tasks_assigned_count || 0}</div>
                <div className="stat-label">Tasks Assigned</div>
              </div>

              <div 
                className={`stat-item completed ${user.tasks_completed_count > 0 ? 'clickable' : ''}`}
                onClick={() => handleTaskStatClick(user, 'completed', user.tasks_completed_count)}
                title={user.tasks_completed_count > 0 ? 'Click to view completed tasks' : 'No completed tasks'}
              >
                <div className="stat-number">{user.tasks_completed_count || 0}</div>
                <div className="stat-label">Tasks Completed</div>
              </div>

              <div className="stat-item completion-rate">
                <div className="stat-number">
                  {user.tasks_assigned_count > 0 
                    ? Math.round((user.tasks_completed_count / user.tasks_assigned_count) * 100)
                    : 0}%
                </div>
                <div className="stat-label">Completion Rate</div>
              </div>
            </div>

            <div className="user-card-footer">
              <small>
                Member since: {new Date(user.date_joined).toLocaleDateString()}
              </small>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && !loading && !error && (
        <div className="no-data">
          <p>No user data available for the selected time period.</p>
        </div>
      )}

      {/* Task Detail Modal */}
      {taskDetailModal && (
        <div className="task-detail-overlay" onClick={closeTaskDetailModal}>
          <div className="task-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-detail-header">
              <h2>
                {taskDetailModal.taskTypeLabel} Tasks - {taskDetailModal.user.username}
              </h2>
              <div className="modal-header-info">
                <span className="task-count">{taskDetailModal.count} tasks</span>
                <span className="filter-period">{getFilterDisplayText()}</span>
              </div>
              <button className="close-btn" onClick={closeTaskDetailModal}>✕</button>
            </div>

            <div className="task-detail-content">
              {modalLoading ? (
                <div className="modal-loading">Loading tasks...</div>
              ) : modalError ? (
                <div className="modal-error">
                  <p>Error loading tasks: {modalError}</p>
                  <button 
                    onClick={() => fetchUserTasks(taskDetailModal.user.id, taskDetailModal.taskType)}
                    className="retry-btn"
                  >
                    Retry
                  </button>
                </div>
              ) : modalTasks.length === 0 ? (
                <div className="modal-no-data">
                  <p>No {taskDetailModal.taskTypeLabel.toLowerCase()} tasks found for this period.</p>
                </div>
              ) : (
                <div className="tasks-list">
                  {modalTasks.map((task) => (
                    <div key={task.id} className="task-item">
                      <div className="task-header">
                        <span className="task-id">#{task.id}</span>
                        <span className={`task-status ${getStatusColor(task.status)}`}>
                          {task.status || 'No Status'}
                        </span>
                      </div>
                      
                      <div className="task-title">
                        {task.title || 'Untitled Task'}
                      </div>
                      
                      {task.description && (
                        <div className="task-description">
                          {task.description.length > 150 
                            ? `${task.description.substring(0, 150)}...` 
                            : task.description}
                        </div>
                      )}
                      
                      <div className="task-meta">
                        {task.client && (
                          <span className="task-client">Client: {task.client}</span>
                        )}
                        {task.priority && (
                          <span className={`task-priority priority-${task.priority.toLowerCase()}`}>
                            {task.priority}
                          </span>
                        )}
                        {task.task_type && (
                          <span className="task-type">Type: {task.task_type}</span>
                        )}
                      </div>
                      
                      <div className="task-dates">
                        <span className="created-date">
                          Created: {formatDate(task.created_at)}
                        </span>
                        {task.due_date && (
                          <span className="due-date">
                            Due: {formatDate(task.due_date)}
                            {task.due_time && ` at ${task.due_time}`}
                          </span>
                        )}
                      </div>
                      
                      {task.assignees && task.assignees.length > 0 && (
                        <div className="task-assignees">
                          <span className="assignees-label">Assignees: </span>
                          {task.assignees.map((assignee, index) => (
                            <span key={assignee.id} className="assignee-name">
                              {assignee.username || assignee.name}
                              {index < task.assignees.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                       <div className="task-assignees">
                          <span className="assignees-label">Assigned By:</span>
                          <span className="assigned-name">
                          {task.creator ? task.creator.username : "N/A"}
                          </span>
                        </div>
                      
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .user-data-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 30px;
          text-align: center;
        }

        .page-header h1 {
          color: white;
          font-size: 1.5rem;
          margin-bottom: 20px;
          display: flex;
        }

        .page-header p {
          color: white;
          margin: 0;
        }

        .filter-section {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 30px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .filter-content {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .filter-title {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .filter-title h3 {
          margin: 0;
          color: #333;
          font-size: 18px;
        }

        .current-filter {
          background-color: #004e92;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }

        .filter-controls-inline {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .filter-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 8px 16px;
          border: 2px solid #e5e7eb;
          background: white;
          color: #374151;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          font-size: 14px;
        }

        .filter-btn:hover {
          border-color: #41295a;
          color: #41295a;
        }

        .filter-btn.active {
          background: #004e92;
          border-color: #004e92;
          color: white;
        }

        .filter-inputs {
          display: flex;
          align-items: center;
        }

        .date-input {
          padding: 8px 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          color: #374151;
          background: white;
          min-width: 140px;
        }

        .date-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .year-input {
          min-width: 100px;
        }

        .user-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }

        .user-stat-card {
          background: rgba(255, 255, 255, 0.4);
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          padding: 20px;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .user-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.15);
        }

        .user-card-header {
          display: flex;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #eee;
        }

        .user-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #000428;
          background: -webkit-linear-gradient(to right, #004e92, #000428);
          background: linear-gradient(to right, #004e92, #000428);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 18px;
          margin-right: 15px;
        }

        .user-info h3 {
          margin: 0 0 4px 0;
          color: white;
          font-size: 18px;
        }

        .user-email {
          margin: 0 0 8px 0;
          color: #666;
          font-size: 14px;
        }

        .user-role {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .user-role.admin {
          background: #fef3c7;
          color: #d97706;
        }

        .user-role.user {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .user-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }

        .stat-item {
          text-align: center;
          padding: 15px;
          border-radius: 8px;
          transition: all 0.2s;
          position: relative;
        }

        .stat-item.clickable {
          cursor: pointer;
          transform: scale(1);
        }

        .stat-item.clickable:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .stat-item.created {
          background: #f0f9ff;
          border: 1px solid #e0f2fe;
        }

        .stat-item.assigned {
          background: #fef7cd;
          border: 1px solid #fde68a;
        }

        .stat-item.completed {
          background: #dcfce7;
          border: 1px solid #bbf7d0;
        }

        .stat-item.completion-rate {
          background: #f3e8ff;
          border: 1px solid #e9d5ff;
        }

        .stat-number {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .stat-item.created .stat-number {
          color: #0369a1;
        }

        .stat-item.assigned .stat-number {
          color: #d97706;
        }

        .stat-item.completed .stat-number {
          color: #16a34a;
        }

        .stat-item.completion-rate .stat-number {
          color: #9333ea;
        }

        .stat-label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .user-card-footer {
          text-align: center;
          padding-top: 10px;
          border-top: 1px solid #eee;
        }

        .user-card-footer small {
          color: white;
        }

        .loading, .no-data {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error-container {
          display: flex;
          justify-content: center;
          padding: 40px;
        }

        .error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 30px;
          max-width: 600px;
          text-align: left;
        }

        .error h3 {
          color: #dc2626;
          margin-top: 0;
          margin-bottom: 15px;
        }

        .error p {
          color: #dc2626;
          margin-bottom: 20px;
        }

        .error-actions {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .retry-btn, .test-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .retry-btn {
          background: #dc2626;
          color: white;
        }

        .retry-btn:hover {
          background: #b91c1c;
        }

        .test-btn {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .test-btn:hover {
          background: #e5e7eb;
        }

        .troubleshooting {
          background: #f9fafb;
          padding: 15px;
          border-radius: 6px;
          border-left: 4px solid #dc2626;
        }

        .troubleshooting h4 {
          margin-top: 0;
          margin-bottom: 10px;
          color: #374151;
        }

        .troubleshooting ul {
          margin: 0;
          padding-left: 20px;
          color: #6b7280;
        }

        .troubleshooting li {
          margin-bottom: 5px;
        }

        /* Task Detail Modal Styles */
        .task-detail-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10000;
          padding: 20px;
        }

        .task-detail-modal {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }

        .task-detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #004e92;
          border-radius: 12px 12px 0 0;
        }

        .task-detail-header h2 {
          margin: 0;
          color: white;
          font-size: 20px;
          font-weight: 800;
        }

        .modal-header-info {
          display: block;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }

        .task-count {
          background: white;
          color: #004e92;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 14px;
          margin-right: 20px;
          font-weight: 600;
        }

        .filter-period {
          color: white;
          margin-right: 20px;
          font-size: 12px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #1f2937;
        }

        .task-detail-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .modal-loading {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }

        .modal-error {
          text-align: center;
          padding: 40px;
          color: #dc2626;
        }

        .modal-no-data {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }

        .tasks-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .task-item {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.2s;
        }

        .task-item:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .task-id {
          font-weight: bold;
          color: #3b82f6;
          font-size: 14px;
        }

        .task-status {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        /* Status Colors */
        .status-assigned {
          background: #fef3c7;
          color: #d97706;
        }

        .status-not-started {
          background: #f3f4f6;
          color: #6b7280;
        }

        .status-in-progress {
          background: #dbeafe;
          color: #2563eb;
        }

        .status-team-review {
          background: #fef7cd;
          color: #ca8a04;
        }

        .status-client-review {
          background: #fed7d7;
          color: #e53e3e;
        }

        .status-rejected-team {
          background: #fee2e2;
          color: #dc2626;
        }

        .status-rejected-client {
          background: #fecaca;
          color: #b91c1c;
        }

        .status-approved {
          background: #d1fae5;
          color: #059669;
        }

        .status-posted {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-completed {
          background: #bbf7d0;
          color: #15803d;
        }

        .status-default {
          background: #f3f4f6;
          color: #6b7280;
        }

        .task-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
          font-size: 16px;
          line-height: 1.4;
        }

        .task-description {
          color: #6b7280;
          margin-bottom: 12px;
          font-size: 14px;
          line-height: 1.5;
        }

        .task-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
        }

        .task-client {
          background: #e0e7ff;
          color: #3730a3;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .task-priority {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .priority-high {
          background: #fee2e2;
          color: #dc2626;
        }

        .priority-medium {
          background: #fef3c7;
          color: #d97706;
        }

        .priority-low {
          background: #d1fae5;
          color: #059669;
        }

        .task-type {
          background: #f3e8ff;
          color: #7c3aed;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .task-dates {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 8px;
          font-size: 13px;
          color: #6b7280;
        }

        .created-date {
          display: flex;
          align-items: center;
        }

        .due-date {
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .task-assignees {
          font-size: 13px;
          color: #6b7280;
        }

        .assignees-label {
          font-weight: 500;
        }

        .assignee-name {
          color: #3b82f6;
          font-weight: 500;
        }
         .assigned-name {
          color: #3b82f6;
          font-weight: 500;
          margin-left: 5px;
         }

        /* Dark Mode Styles */
        body.dark-mode .user-data-container {
          color: #e5e5e5;
        }

        body.dark-mode .page-header h1 {
          color: #e5e5e5;
        }

        body.dark-mode .page-header p {
          color: #a0a0a0;
        }

        body.dark-mode .filter-section {
          background: #2d2d2d;
          border: 1px solid #404040;
        }

        body.dark-mode .filter-title h3 {
          color: #e5e5e5;
        }

        body.dark-mode .current-filter {
          background: #0c4a6e;
          color: #38bdf8;
        }

        body.dark-mode .filter-btn {
          background: #404040;
          border-color: #525252;
          color: #e5e5e5;
        }

        body.dark-mode .filter-btn:hover {
          border-color: #3b82f6;
          color: #60a5fa;
        }

        body.dark-mode .filter-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        body.dark-mode .date-input {
          background: #404040;
          border-color: #525252;
          color: #e5e5e5;
        }

        body.dark-mode .date-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        body.dark-mode .user-stat-card {
          background: #2d2d2d;
          border: 1px solid #404040;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        body.dark-mode .user-stat-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
        }

        body.dark-mode .user-card-header {
          border-bottom: 1px solid #404040;
        }

        body.dark-mode .user-info h3 {
          color: #e5e5e5;
        }

        body.dark-mode .user-email {
          color: #a0a0a0;
        }

        body.dark-mode .user-role.admin {
          background: #451a03;
          color: #fbbf24;
        }

        body.dark-mode .user-role.user {
          background: #1e3a8a;
          color: #60a5fa;
        }

        body.dark-mode .stat-item.created {
          background: #0c4a6e;
          border: 1px solid #075985;
        }

        body.dark-mode .stat-item.assigned {
          background: #451a03;
          border: 1px solid #92400e;
        }

        body.dark-mode .stat-item.completed {
          background: #14532d;
          border: 1px solid #166534;
        }

        body.dark-mode .stat-item.completion-rate {
          background: #581c87;
          border: 1px solid #7c3aed;
        }

        body.dark-mode .stat-item.created .stat-number {
          color: #38bdf8;
        }

        body.dark-mode .stat-item.assigned .stat-number {
          color: #fbbf24;
        }

        body.dark-mode .stat-item.completed .stat-number {
          color: #22c55e;
        }

        body.dark-mode .stat-item.completion-rate .stat-number {
          color: #a855f7;
        }

        body.dark-mode .stat-label {
          color: #a0a0a0;
        }

        body.dark-mode .user-card-footer {
          border-top: 1px solid #404040;
        }

        body.dark-mode .user-card-footer small {
          color: #a0a0a0;
        }

        body.dark-mode .loading,
        body.dark-mode .no-data {
          color: #a0a0a0;
        }

        body.dark-mode .task-detail-modal {
          background: #2d2d2d;
          border: 1px solid #404040;
        }

        body.dark-mode .task-detail-header {
          background: #1f1f1f;
          border-bottom: 1px solid #404040;
        }

        body.dark-mode .task-detail-header h2 {
          color: #e5e5e5;
        }

        body.dark-mode .task-count {
          background: #3b82f6;
          color: white;
        }

        body.dark-mode .filter-period {
          color: #a0a0a0;
        }

        body.dark-mode .close-btn {
          color: #a0a0a0;
        }

        body.dark-mode .close-btn:hover {
          background: #404040;
          color: #e5e5e5;
        }

        body.dark-mode .modal-loading,
        body.dark-mode .modal-error,
        body.dark-mode .modal-no-data {
          color: #a0a0a0;
        }

        body.dark-mode .task-item {
          background: #1f1f1f;
          border: 1px solid #404040;
        }

        body.dark-mode .task-item:hover {
          background: #2d2d2d;
          border-color: #525252;
        }

        body.dark-mode .task-title {
          color: #e5e5e5;
        }

        body.dark-mode .task-description {
          color: #a0a0a0;
        }

        body.dark-mode .task-dates {
          color: #a0a0a0;
        }

        body.dark-mode .task-assignees {
          color: #a0a0a0;
        }

        body.dark-mode .assignee-name {
          color: #60a5fa;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .filter-controls-inline {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }
          
          .filter-buttons {
            flex-direction: column;
            width: 100%;
          }
          
          .filter-btn {
            width: 100%;
          }
          
          .filter-inputs {
            width: 100%;
          }
          
          .date-input {
            width: 100%;
            min-width: auto;
          }
          
          .filter-title {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .task-detail-modal {
            margin: 10px;
            max-width: calc(100vw - 20px);
          }

          .task-detail-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .modal-header-info {
            flex-direction: row;
            align-items: center;
            gap: 12px;
          }

          .task-meta {
            flex-direction: column;
            gap: 8px;
          }

          .task-dates {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default UserData;